/* nimbus-motion.js
 * GSAP 3 + ScrollTrigger animation engine for Nimbus / Sugar Browed.
 * Requires (deferred, in order):
 *   gsap.min.js, ScrollTrigger.min.js, nimbus-motion-tokens.js
 *
 * Pipelines, in order:
 *   1. initSectionModules    — primary path. [data-motion-section="<name>"]
 *   2. initLegacyAutoReveal  — fallback for sections that still ship .nimbus-anim
 *      (basic GSAP fade+rise — far classier than the old CSS !important system,
 *       which has been removed from base.css)
 *   3. initParallax / initEffects — scroll-driven extras
 *
 * Public API:
 *   window.NimbusMotion.register(name, factory)
 *   window.NimbusMotion.refresh()
 *   window.NimbusMotion.cleanup(scopeEl)
 *   window.NimbusMotion.initSection(rootEl)
 */
(function () {
  'use strict';

  const NM = window.NimbusMotion = window.NimbusMotion || {};
  const T  = () => NM.tokens;

  /* ─── Internal state ────────────────────────────────────────────── */
  const _registry   = {};
  const _contexts   = new Map();
  const _refreshTmr = { id: null };
  NM._contexts = _contexts;

  /* ─── Breakpoints ───────────────────────────────────────────────── */
  const MM = {
    isMobile:      '(max-width: 749px)',
    isTablet:      '(min-width: 750px) and (max-width: 1199px)',
    isDesktop:     '(min-width: 1200px)',
    reduced:       '(prefers-reduced-motion: reduce)',
    motionMobile:  '(prefers-reduced-motion: no-preference) and (max-width: 749px)',
    motionTablet:  '(prefers-reduced-motion: no-preference) and (min-width: 750px) and (max-width: 1199px)',
    motionDesktop: '(prefers-reduced-motion: no-preference) and (min-width: 1200px)',
    motionWide:    '(prefers-reduced-motion: no-preference) and (min-width: 750px)'
  };
  NM.MM = MM;

  /* ─── GSAP setup ────────────────────────────────────────────────── */
  function setup() {
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });
    gsap.config({ nullTargetWarn: false });
    gsap.defaults({ ease: T().ease.ease, duration: T().duration.base });
  }

  NM.register = function (name, factory) { _registry[name] = factory; };

  NM.refresh = function () {
    clearTimeout(_refreshTmr.id);
    _refreshTmr.id = setTimeout(() => ScrollTrigger.refresh(), 200);
  };

  NM.cleanup = function (scopeEl) {
    const ctx = _contexts.get(scopeEl);
    if (ctx) {
      if (typeof ctx.revert === 'function') ctx.revert();
      _contexts.delete(scopeEl);
    }
    /* Also kill orphaned STs whose trigger lives inside scope */
    ScrollTrigger.getAll()
      .filter(s => s.trigger && scopeEl.contains(s.trigger))
      .forEach(s => s.kill());
  };

  function aboveFold(el) {
    return el.getBoundingClientRect().top < window.innerHeight - 80;
  }

  /* ═══════════════════════════════════════════════════════════════
   * 1. Named section modules (primary)
   * ═══════════════════════════════════════════════════════════════ */
  function initSectionModules(root) {
    const els = root === document
      ? document.querySelectorAll('[data-motion-section]')
      : (root.dataset && root.dataset.motionSection
          ? [root]
          : root.querySelectorAll('[data-motion-section]'));

    els.forEach(el => {
      if (_contexts.has(el)) return;
      const name    = el.dataset.motionSection;
      const factory = _registry[name];
      if (!factory) return;

      const ctx = gsap.context(() => factory(el, gsap.matchMedia()));
      _contexts.set(el, ctx);
    });
  }

  /* ═══════════════════════════════════════════════════════════════
   * 2. Legacy auto-reveal — anything with .nimbus-anim that does NOT
   *    have a data-motion-section attribute. Default: subtle GSAP
   *    fade+rise stagger on [data-anim-order] children, or on the
   *    section itself if no ordered children exist.
   * ═══════════════════════════════════════════════════════════════ */
  function initLegacyAutoReveal(root) {
    const isAnim = (el) => el.classList && el.classList.contains('nimbus-anim');
    const els = root === document
      ? [...document.querySelectorAll('.nimbus-anim')]
      : (isAnim(root) ? [root] : [...root.querySelectorAll('.nimbus-anim')]);

    els.forEach(section => {
      if (_contexts.has(section)) return;
      if (section.hasAttribute('data-motion-section')) return; /* module owns it */

      const ctx = gsap.context(() => {
        const mm = gsap.matchMedia();

        mm.add(MM.reduced, () => { /* show as-is */ });

        mm.add(`${MM.motionMobile}, ${MM.motionWide}`, () => {
          const ordered = [...section.querySelectorAll('[data-anim-order]')]
            .sort((a, b) => Number(a.dataset.animOrder) - Number(b.dataset.animOrder));

          const targets = ordered.length ? ordered : [section];
          const isWide  = window.matchMedia('(min-width: 750px)').matches;

          gsap.set(targets, { opacity: 0, y: isWide ? 36 : 22 });

          gsap.to(targets, {
            opacity: 1,
            y: 0,
            duration: isWide ? 0.95 : 0.7,
            stagger: isWide ? 0.08 : 0.06,
            ease: 'expo.out',
            scrollTrigger: {
              trigger: section,
              start: 'top 84%',
              toggleActions: 'play none none none',
              once: true
            }
          });
        });
      });
      _contexts.set(section, ctx);
    });
  }

  /* ═══════════════════════════════════════════════════════════════
   * 3. Parallax + global effects
   * ═══════════════════════════════════════════════════════════════ */
  function initParallax(root) {
    if (NM.parallax && typeof NM.parallax.init === 'function') NM.parallax.init(root);
  }
  function initEffects(root) {
    if (NM.effects && typeof NM.effects.init === 'function') NM.effects.init(root);
  }

  NM.initSection = function (root) {
    initSectionModules(root);
    initLegacyAutoReveal(root);
    initParallax(root);
    initEffects(root);
  };

  /* ─── Shopify lifecycle ─────────────────────────────────────────── */
  function bindShopifyEvents() {
    document.addEventListener('shopify:section:load', (e) => {
      NM.initSection(e.target);
      NM.refresh();
    });
    document.addEventListener('shopify:section:unload', (e) => NM.cleanup(e.target));
    document.addEventListener('shopify:section:select', () => NM.refresh());
    document.addEventListener('shopify:block:select',   () => NM.refresh());
  }

  /* ─── Boot ──────────────────────────────────────────────────────── */
  function boot() {
    setup();

    /* Neutralise Dawn's scroll-trigger CSS so our motion isn't fighting it */
    document.querySelectorAll('.scroll-trigger--offscreen')
      .forEach(el => el.classList.remove('scroll-trigger--offscreen'));
    document.querySelectorAll('.scroll-trigger:not(.scroll-trigger--animate)')
      .forEach(el => el.classList.add('scroll-trigger--animate'));

    /* Hero — uses .is-revealed CSS pattern in its own section CSS.
     * Logo banner content (.nimbus-logo-banner__content) is intentionally
     * NOT touched here — it has its own scroll-storytelling script that
     * reveals it only after the wordmark has morphed into the header. */
    document.querySelectorAll('.sb-hero').forEach(el => {
      if (aboveFold(el)) {
        requestAnimationFrame(() => el.classList.add('is-revealed'));
      } else {
        ScrollTrigger.create({
          trigger: el,
          start: 'top 85%',
          once: true,
          onEnter: () => el.classList.add('is-revealed')
        });
      }
    });

    NM.initSection(document);
    bindShopifyEvents();

    /* Staggered ScrollTrigger refreshes — lazy images can load at different
     * times and cause layout shifts that stale trigger positions.
     * Three passes cover: fast devices (200ms), slow connections (600ms),
     * very slow / first-visit with many images (1500ms). */
    setTimeout(function () { ScrollTrigger.refresh(); }, 200);
    setTimeout(function () { ScrollTrigger.refresh(); }, 600);
    setTimeout(function () { ScrollTrigger.refresh(); }, 1500);
  }

  /* ── Boot timing fix ─────────────────────────────────────────────────
   * Deferred scripts execute in order, but on iOS Safari with a warm cache
   * readyState can already be 'complete' when nimbus-motion.js runs — which
   * means boot() would fire before nimbus-motion-modules.js has had a chance
   * to register its section factories.  A single setTimeout(0) yields back
   * to the browser's task queue, letting all deferred scripts in the same
   * <head> run first, so the factory registry is fully populated by the time
   * boot() is called.
   * ─────────────────────────────────────────────────────────────────────── */
  /* runSafetyNet — clears any GSAP inline states that didn't get animated
   * (ScrollTrigger never fired, bfcache restore, slow device, etc.).
   * Called both on boot and on bfcache page restore (pageshow persisted). */
  function runSafetyNet() {
    document.querySelectorAll('[data-motion-section], .nimbus-anim').forEach(function (section) {
      var toReveal = [section].concat(
        Array.from(section.querySelectorAll('*'))
      ).filter(function (el) {
        var s = el.style;
        return (s.opacity === '0') ||
               (s.clipPath && s.clipPath.indexOf('100%') !== -1) ||
               (s.webkitClipPath && s.webkitClipPath.indexOf('100%') !== -1) ||
               (s.transform && s.transform.indexOf('translateY') !== -1);
      });
      toReveal.forEach(function (el) {
        el.style.opacity          = '';
        el.style.transform        = '';
        el.style.clipPath         = '';
        el.style.webkitClipPath   = '';
        el.style.visibility       = '';
        el.style.letterSpacing    = ''; /* clear stuck letterTrack state */
      });
    });
  }

  function safeBoot() {
    boot();
    /* Safety net: force-reveal stuck elements after a generous delay */
    setTimeout(runSafetyNet, 2500);
  }

  /* bfcache (back-forward cache) restore — iOS Safari freezes the page in
   * memory when navigating away, then restores it on back/forward. GSAP
   * initial states (opacity:0, clip-path) are preserved but scroll triggers
   * may not re-fire at the correct positions. Refresh + safety net on restore. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) {
      /* Small delay so the browser has finished painting the restored page */
      setTimeout(function () {
        ScrollTrigger.refresh();
        /* Give triggers 300ms to fire before running the safety net */
        setTimeout(runSafetyNet, 300);
      }, 50);
    }
  });

  if (document.readyState === 'complete') {
    /* Already loaded — defer one task tick so module factories can register */
    setTimeout(safeBoot, 0);
  } else {
    document.addEventListener('DOMContentLoaded', safeBoot);
  }
})();
